<template>
  <!-- Control mode: full media player panel -->
  <MediaPlayerControl v-if="mode === 'control'" :device="device" />

  <!-- Grid mode: delegate to DeviceGridCell with media icon -->
  <DeviceGridCell
    v-else-if="mode === 'grid'"
    :icon="device.icon || (isTouchPanel ? 'tablet' : 'library_music')"
    :name="displayName"
    :is-on="false"
    active-color="amber-400"
    @toggle="$emit('toggle')"
    @long-press="$emit('open-control-modal')" />

  <!-- Icon mode (P14): the drawn transport in the status ring — the media
       family's own purple, the same tone the media sheet wears. Playing fills
       the ring, an idle / paused player keeps the track alone. -->
  <template v-else-if="mode === 'icon'">
    <div
      class="device-icon-pin"
      :class="sizeClass"
      :style="{ '--pin-active-color': MEDIA_PIN_ACCENT }"
      :title="device.deviceName">
      <ControlStatusRing :value="isLivePlaying ? 100 : 0" :accent="MEDIA_PIN_ACCENT">
        <ControlIcon
          :name="isLivePlaying ? 'play' : 'pause'"
          :size="pinIconSize"
          :style="{ color: MEDIA_PIN_ACCENT }" />
      </ControlStatusRing>
      <div v-if="isLivePlaying" class="device-icon-pin__glow"></div>
    </div>
  </template>

  <!-- Any other mode: fall back to GenericWidget with proper icon -->
  <GenericWidget
    v-else
    :device="{ ...device, icon: device.icon || (isTouchPanel ? 'tablet' : 'library_music') }"
    :mode="mode"
    :size="size"
    :fluid="fluid"
    :display-mode="displayMode"
    @toggle="$emit('toggle')"
    @open-control-modal="$emit('open-control-modal')"
    @value-update="(...a) => $emit('value-update', a[0], a[1])" />
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted } from 'vue';
import MediaPlayerControl from '../controls/MediaPlayerControl.vue';
import GenericWidget from './GenericWidget.vue';
import DeviceGridCell from '../panel/DeviceGridCell.vue';
import ControlIcon from '../device-icons/ControlIcon.vue';
import ControlStatusRing from '../device-sheet/ControlStatusRing.vue';
import { useMediaPlayersStore } from '../../stores/mediaPlayers';
import { useTouchPanelStore } from '../../stores/touchPanel';
import type { Device } from '@/types';

interface Props {
  device: Device;
  mode?: 'icon' | 'control' | 'grid';
  size?: 'sm' | 'md' | 'lg';
  fluid?: boolean;
  displayMode?: 'full' | 'room';
}

const props = withDefaults(defineProps<Props>(), {
  mode: 'icon',
});

defineEmits<{
  toggle: [];
  'open-control-modal': [];
  'view-stream': [];
  'view-details': [];
  'value-update': [valueId: string, newValue: unknown];
}>();

const store = useMediaPlayersStore();
const panelStore = useTouchPanelStore();

const isTouchPanel = computed(
  () =>
    props.device.deviceControl === 'touch_panel' ||
    props.device.controlType === 'touch_panel' ||
    props.device.deviceType === 'otto_touch_panel',
);

const displayName = computed(() =>
  props.displayMode === 'full' ? props.device.deviceName : props.device.shortName || props.device.deviceName,
);

/** The media family's own purple — the same tone `MediaPlayerControl`'s sheet wears. */
const MEDIA_PIN_ACCENT = 'rgb(192, 132, 252)';

const sizeClass = computed(() => (props.fluid ? 'device-icon-pin--fluid' : `device-icon-pin--${props.size ?? 'md'}`));

/** The drawn transport's size inside the pin (P14), in the `GenericIcon` steps. */
const pinIconSize = computed(() => {
  if (props.fluid) return '28px';
  switch (props.size) {
    case 'lg':
      return '28px';
    case 'sm':
      return '16px';
    default:
      return '22px';
  }
});

// Read a device value by key (touch panels get live media values mirrored
// from the panel's own playback state by the hub).
function deviceValue(key: string): unknown {
  return props.device.deviceValues?.find((v) => v.valueKey === key)?.currentValue;
}

let panelsRequested = false;
async function ensurePanelsLoaded() {
  if (isTouchPanel.value && !panelsRequested) {
    panelsRequested = true;
    try {
      if (panelStore.panels.length === 0) await panelStore.loadPanels();
    } catch {
      /* ignore */
    }
  }
}

// Find the linked media-player row (deviceId → mediaPlayer) for live playback state
const mediaPlayer = computed(() => store.players.find((p) => p.deviceId === props.device.id) || null);

const playerId = computed(() => mediaPlayer.value?.id || null);

const playbackState = computed(() => (playerId.value ? store.playbackStates.get(playerId.value) || null : null));

// Touch panels report playback via their device values (play_state/media_*)
const touchPlayState = computed(() => (isTouchPanel.value ? String(deviceValue('play_state') || '') : ''));

const isLivePlaying = computed(() =>
  isTouchPanel.value
    ? touchPlayState.value === 'playing' || touchPlayState.value === 'buffering'
    : !!playbackState.value?.isPlaying,
);

let pollInterval: ReturnType<typeof setInterval> | null = null;

// Load live state, then keep it fresh with a light poll (the hub has no
// socket push for playback state, so the widget refreshes itself).
// Touch panels don't need polling: their hub-mirrored device values update
// via the normal device_value_changed socket.
onMounted(async () => {
  if (isTouchPanel.value) {
    await ensurePanelsLoaded();
    return;
  }
  try {
    await store.initialize();
    if (playerId.value) {
      if (!store.playbackStates.has(playerId.value)) {
        await store.fetchPlaybackState(playerId.value);
      }
      pollInterval = setInterval(() => {
        if (playerId.value) store.fetchPlaybackState(playerId.value);
      }, 10000);
    }
  } catch {
    /* ignore — stays in static/device-value mode */
  }
});

onUnmounted(() => {
  if (pollInterval) clearInterval(pollInterval);
});
</script>

<style scoped>
/* Album-art background inside the device button */
.media-btn__bg {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  filter: blur(3px) brightness(0.55) saturate(1.2);
  transform: scale(1.12);
  z-index: 0;
}

.media-btn__overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.25) 0%, rgba(0, 0, 0, 0.55) 100%);
  z-index: 1;
}

.media-btn__icon {
  position: relative;
  z-index: 2;
}

.media-btn__info {
  position: relative;
  z-index: 2;
  /* Keep the status text clear of the quick play/pause button */
  padding-right: 3.2em;
}

.media-btn--has-art .device-icon span {
  color: #fff !important;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
}

.media-btn--has-art .device-name,
.media-btn--has-art .device-current-value {
  color: #fff !important;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.7);
}

/* Quick play/pause button */
.media-btn__quick {
  position: absolute;
  right: 0.6em;
  bottom: 0.6em;
  z-index: 20;
  width: 2.1em;
  height: 2.1em;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.92);
  color: #111;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  transition:
    transform 0.15s ease,
    background 0.15s ease;
  cursor: pointer;
  border: none;
}

.media-btn__quick:hover {
  transform: scale(1.08);
  background: #fff;
}

.media-btn__quick .material-symbols-outlined {
  font-size: 1.15em;
  line-height: 1;
}

/* Equalizer bars while playing */
.media-btn__eq {
  display: inline-flex;
  align-items: flex-end;
  gap: 2px;
  height: 0.85em;
  margin-right: 0.3em;
  vertical-align: baseline;
}

.media-btn__eq i {
  width: 2px;
  background: currentColor;
  border-radius: 1px;
  animation: media-eq 1s ease-in-out infinite;
}

.media-btn__eq i:nth-child(1) {
  height: 40%;
  animation-delay: 0s;
}
.media-btn__eq i:nth-child(2) {
  height: 90%;
  animation-delay: 0.25s;
}
.media-btn__eq i:nth-child(3) {
  height: 60%;
  animation-delay: 0.5s;
}

@keyframes media-eq {
  0%,
  100% {
    transform: scaleY(0.5);
  }
  50% {
    transform: scaleY(1);
  }
}
</style>
